import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

interface TrustIDVerificationProps {
  counsellorId: string;
  trustIdData?: {
    verified: boolean;
    verifiedDate?: string;
    verifiedBy?: string;
    supportingDocument?: {
      name: string;
      url: string;
      originalName: string;
    };
    notes?: string;
  };
  onUpdate: () => void;
}

const TrustIDVerification: React.FC<TrustIDVerificationProps> = ({
  counsellorId,
  trustIdData,
  onUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notes, setNotes] = useState(trustIdData?.notes || '');
  const [supportingDoc, setSupportingDoc] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      console.log('File selected:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      });
      setSupportingDoc(file);
    }
  };

  const handleVerify = async () => {
    if (!supportingDoc) {
      setError('Supporting document is required');
      return;
    }

    if (!notes.trim()) {
      setError('Notes are required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      console.log('handleVerify - supportingDoc state:', {
        exists: !!supportingDoc,
        name: supportingDoc?.name,
        size: supportingDoc?.size,
        type: supportingDoc?.type
      });

      const formData = new FormData();
      formData.append('verified', 'true');
      formData.append('notes', notes);
      if (supportingDoc) {
        formData.append('supportingDocument', supportingDoc);
        console.log('File appended to FormData');
      } else {
        console.error('No supportingDoc to append!');
      }

      // Debug: Log FormData contents
      console.log('FormData contents:', {
        verified: formData.get('verified'),
        notes: formData.get('notes'),
        supportingDocument: formData.get('supportingDocument'),
        supportingDocType: supportingDoc?.type,
        supportingDocSize: supportingDoc?.size
      });

      await api.post(`/api/counsellors/${counsellorId}/trustid-verification`, formData, {
        transformRequest: [(data) => data], // Prevent axios from transforming FormData
      });

      setSuccess('TrustID verification completed successfully');
      setDialogOpen(false);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to verify TrustID');
    } finally {
      setLoading(false);
    }
  };

  const handleUnverify = async () => {
    if (!window.confirm('Are you sure you want to remove TrustID verification?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      await api.delete(`/api/counsellors/${counsellorId}/trustid-verification`);

      setSuccess('TrustID verification removed');
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove verification');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async () => {
    if (!trustIdData?.supportingDocument?.url) return;

    try {
      // Get presigned URL from backend
      const response = await api.get(`/api/counsellors/${counsellorId}/trustid-verification/download`);
      const { url } = response.data;
      
      // Download using the presigned URL
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', trustIdData.supportingDocument.originalName || 'document.pdf');
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download document');
    }
  };

  return (
    <>
      <Box
        sx={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          p: 3,
          mb: 3,
        }}
      >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 500 }}>
          TrustID Verification
        </Typography>
        {trustIdData?.verified ? (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: '16px' }} />}
            label="Verified"
            sx={{
              backgroundColor: 'rgba(76, 175, 80, 0.2)',
              color: '#4caf50',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              fontWeight: 500,
              fontSize: '0.8125rem',
              height: '24px',
              '& .MuiChip-icon': {
                marginLeft: '8px',
              },
            }}
          />
        ) : (
          <Chip
            icon={<CancelIcon sx={{ fontSize: '16px' }} />}
            label="Not Verified"
            sx={{
              backgroundColor: '#f44336',
              color: 'white',
              fontWeight: 500,
              fontSize: '0.8125rem',
              height: '24px',
              '& .MuiChip-icon': {
                marginLeft: '8px',
                color: 'white',
              },
            }}
          />
        )}
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            color: 'white',
            border: '1px solid rgba(244, 67, 54, 0.3)',
          }} 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 2,
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            color: 'white',
            border: '1px solid rgba(76, 175, 80, 0.3)',
          }} 
          onClose={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}

      {trustIdData?.verified ? (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 0.5 }}>
                Verified Date:
              </Typography>
              <Typography variant="body1" sx={{ color: 'white' }}>
                {trustIdData.verifiedDate
                  ? new Date(trustIdData.verifiedDate).toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Grid>

            {trustIdData.supportingDocument && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 0.5 }}>
                  Supporting Document:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon sx={{ color: '#2196f3' }} />
                  <Button
                    size="small"
                    onClick={handleDownloadDocument}
                    sx={{
                      color: '#2196f3',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                      },
                    }}
                  >
                    {trustIdData.supportingDocument.originalName}
                  </Button>
                </Box>
              </Grid>
            )}

            {trustIdData.notes && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 0.5 }}>
                  Notes:
                </Typography>
                <Typography variant="body1" sx={{ color: 'white' }}>
                  {trustIdData.notes}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleUnverify}
              disabled={loading}
              startIcon={<DeleteIcon />}
              sx={{
                color: '#f44336',
                borderColor: 'rgba(244, 67, 54, 0.5)',
                '&:hover': {
                  borderColor: '#f44336',
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                },
              }}
            >
              Remove Verification
            </Button>
          </Box>
        </Box>
      ) : (
        <Box>
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              color: 'white',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              '& .MuiAlert-icon': {
                color: '#ff9800',
              },
            }}
          >
            <strong>Required:</strong> TrustID verification is mandatory for onboarding completion.
            Please verify the counsellor's identity using TrustID and upload supporting documentation.
          </Alert>

          <Button
            variant="contained"
            onClick={() => setDialogOpen(true)}
            startIcon={<UploadIcon />}
            sx={{
              backgroundColor: '#2196f3',
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#1976d2',
              },
            }}
          >
            Verify Identity
          </Button>
        </Box>
      )}
      </Box>

      {/* Verification Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>TrustID Verification</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
            Please complete the TrustID verification process and upload the supporting documentation.
          </Typography>

          <TextField
            label="Verification Notes"
            fullWidth
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            margin="normal"
            required
            helperText="Describe the verification process and any relevant details"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2196f3',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                backgroundColor: 'rgba(30, 41, 59, 0.95)',
                paddingX: 0.5,
              },
              '& .MuiFormHelperText-root': {
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          />

          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              fullWidth
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              Upload Supporting Document *
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
              />
            </Button>
            {supportingDoc && (
              <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                Selected: {supportingDoc.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDialogOpen(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerify}
            variant="contained"
            disabled={loading || !supportingDoc || !notes.trim()}
            sx={{
              backgroundColor: '#2196f3',
              color: 'white',
              '&:hover': {
                backgroundColor: '#1976d2',
              },
              '&:disabled': {
                backgroundColor: 'rgba(33, 150, 243, 0.3)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm Verification'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TrustIDVerification;
